import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowRight, Check, ChevronDown, ExternalLink, MessageCircle, Phone, RotateCcw, ScanLine, ShieldCheck, Truck,
} from "lucide-react";
import { toast } from "sonner";
import {
  Breadcrumbs, Container, Eyebrow, H1, H2, H3, Section, Spinner, EmptyState,
} from "../components/Primitives";
import { ProductGrid } from "../components/ProductCard";
import { VerificationBadge, SelfDescribedBlock } from "../components/VerificationBadge";
import { CertificateBlock } from "../components/CertificateBlock";
import { GuaranteeLine } from "../components/GuaranteeLine";
import { getDelivery, getProduct, postHoroscopeCheck, apiError } from "../lib/api";
import { useCurrency } from "../lib/currency";
import { useCart } from "../lib/cart";
import { useSEO, breadcrumbSchema } from "../lib/seo";
import { COMPANY } from "../content/nav";
import { ProductContentV2 } from "../components/ProductContentV2";
import {
  AnimatePresence, EASE_OUT, Reveal, motion, panelSwap,
} from "../lib/motion";

const TABS = [
  ["description", "Description"],
  ["tradition", "In the tradition"],
  ["wear", "How to wear"],
  ["care", "Care"],
  ["certificate", "Certificate"],
  ["reviews", "Questions & reviews"],
];

function Gallery({ product }) {
  const [active, setActive] = useState(0);
  const images = product.images || [];
  const current = images[active];
  const hasTypePhoto = images.some((i) => i.type === "type_photo");
  const ambient = current?.ambient_hex || product.images?.find((i) => i.ambient_hex)?.ambient_hex || "#3d2f14";
  return (
    <div data-testid="product-gallery" className="lg:sticky lg:top-28 lg:self-start">
      <div className="flex gap-3">
        <div className="no-scrollbar flex w-14 shrink-0 flex-col gap-2 sm:w-[72px]">
          {images.map((img, i) => (
            <button
              key={`${img.url}-${i}`}
              type="button"
              onClick={() => setActive(i)}
              data-testid={`gallery-thumb-${i}`}
              aria-label={img.alt}
              aria-current={i === active}
              className={`relative overflow-hidden border transition-colors duration-300 ${
                i === active ? "border-gold" : "border-rule hover:border-gold/60"
              }`}
            >
              <img src={img.thumb || img.url} alt="" loading="lazy" className="aspect-square w-full object-cover" />
              {img.type === "xray" && (
                <span className="ui absolute inset-x-0 bottom-0 bg-deep/85 py-px text-center text-[8px] uppercase tracking-[0.1em] text-wash">
                  X-ray
                </span>
              )}
            </button>
          ))}
        </div>

        <div
          className="prod-stage relative aspect-square flex-1"
          style={{ "--ambient": ambient }}
          data-testid="prod-stage"
        >
          <div className="prod-stage__glow" aria-hidden="true" />
          <AnimatePresence mode="wait" initial={false}>
            <motion.img
              key={current?.url}
              src={current?.large || current?.url}
              alt={current?.alt || product.title}
              fetchPriority="high"
              decoding="async"
              initial={{ opacity: 0, scale: 1.015 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.42, ease: EASE_OUT }}
              className="prod-stage__img absolute inset-0 h-full w-full object-contain p-6 sm:p-10"
            />
          </AnimatePresence>
          <div className="prod-stage__vignette" aria-hidden="true" />
          {current?.type === "type_photo" && (
            <span className="prod-stage__chip" data-testid="type-photo-badge"
                  title="This is a representative photograph of this stone type. The photograph of your actual stone is added before dispatch.">
              Representative photo of this stone type
            </span>
          )}
          {current?.type === "xray" && (
            <span className="prod-stage__chip" style={{ background: "rgba(20,19,16,0.7)" }}>
              X-ray · {product.rudraksha?.chambers_visible} chambers countable
            </span>
          )}
          {current?.type === "loose_on_card" && (
            <span className="prod-stage__chip" style={{ background: "rgba(31,107,74,0.85)", color: "#fff" }}>
              The actual item, loose, with a mm scale            </span>
          )}
        </div>
      </div>
      <p className="ui mt-4 text-xs italic leading-[1.7] text-dim" data-testid={product.representative_image ? "representative-image-caption" : hasTypePhoto ? "type-photo-caption" : "gallery-caption"}>
        {product.representative_image
          ? "Representative image — your piece will vary. These are AI-generated stand-ins; real photographs will replace them as each piece is prepared for dispatch."
          : hasTypePhoto
          ? "Representative photograph of this stone type — the photograph of your actual stone is added before dispatch. The certificate and, where applicable, the X-ray for this exact piece are added when each piece is listed."
          : "Photographed out of the packet, on a neutral grey card, with a millimetre scale in frame. No filters and no grading are applied to any image on this page."}
        {product.video_url
          ? " A rotation video under diffuse light — no music — is included in the gallery for every item above ₹10,000."
          : ""}
      </p>
    </div>
  );
}

function SpecTable({ product }) {
  const g = product.gemstone;
  const r = product.rudraksha;
  const c = product.certificate;
  const rows = [];
  if (g) {
    rows.push(
      ["Species / variety", `${g.species}, variety ${g.variety}`],
      ["Weight", `${g.carat} ct · ${g.ratti} ratti (conversion: 1 ratti = ${g.ratti_conversion_factor} carat)`],
      ["Dimensions", `${g.length_mm} × ${g.width_mm} × ${g.depth_mm} mm`],
      ["Shape / cut", g.shape],
      ["Colour", g.colour],
      ["Origin", g.origin],
      ["Treatment", `${g.treatment}. ${g.treatment_detail}`],
      ["Clarity", g.clarity_note],
      ["Specific gravity", g.specific_gravity],
      ["Refractive index", g.refractive_index],
    );
  }
  if (r) {
    rows.push(
      ["Mukhi", `${r.mukhi} mukhi`],
      ["Origin", r.origin === "nepal" ? "Nepal" : "Java, Indonesia"],
      ["Diameter", `${r.diameter_mm} mm`],
      ["Weight", `${r.weight_g} g`],
      ["Capped", r.is_capped ? `Yes — ${r.cap_metal}` : "No, uncapped and undrilled"],
      ["Form", r.form],
      ["X-ray", r.xray_image_url ? `Published, taken ${r.xray_date} · ${r.chambers_visible} chambers visible` : "Not X-rayed — below our ₹3,000 threshold, and we say so rather than imply otherwise"],
    );
  }
  if (c) {
    rows.push(
      ["Laboratory", `${c.lab} — ${c.lab_name}`],
      ["Report number", c.report_number],
      ["Report date", c.report_date],
    );
  }
  rows.push(
    ["SKU", product.sku],
    ["HSN code", product.hsn_code || "Nil — puja samagri"],
    ["GST", `${product.gst_rate}% (included in the price shown)`],
  );
  return (
    <table className="w-full text-sm" data-testid="specification-table">
      <caption className="sr-only">Specification</caption>
      <tbody>
        {rows.map(([k, v], i) => (
          <tr key={k} className={i % 2 ? "bg-wash" : ""}>
            <th scope="row" className="ui w-2/5 border-b border-rule px-3 py-2.5 text-left font-medium text-dim">
              {k}
            </th>
            <td className="num border-b border-rule px-3 py-2.5 text-ink">{v}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function DeliveryBlock() {
  const [country, setCountry] = useState("IN");
  const [postcode, setPostcode] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const check = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      setResult(await getDelivery({ country, postcode }));
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card-s p-5" data-testid="delivery-block">
      <p className="eyebrow mb-3">Delivery date — not a range of days</p>
      <form onSubmit={check} className="flex flex-wrap gap-2">
        <select
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          aria-label="Destination country"
          data-testid="delivery-country"
          className="field ui max-w-[190px]"
        >
          <option value="IN">India</option>
          <option value="US">United States</option>
          <option value="GB">United Kingdom</option>
          <option value="AE">United Arab Emirates</option>
          <option value="CA">Canada</option>
          <option value="AU">Australia</option>
          <option value="DE">Germany (EU)</option>
          <option value="SG">Singapore</option>
        </select>
        {country === "IN" && (
          <input
            value={postcode}
            onChange={(e) => setPostcode(e.target.value)}
            placeholder="Pincode"
            aria-label="Pincode"
            data-testid="delivery-pincode"
            className="field num max-w-[130px]"
          />
        )}
        <button type="submit" disabled={busy} className="btn btn-secondary" data-testid="delivery-check">
          {busy ? "Checking…" : "Show me the date"}
        </button>
      </form>
      {result && (
        <p className="ui mt-4 text-sm text-ink" data-testid="delivery-result">
          Delivered to {result.label} by{" "}
          <span className="num font-medium">
            {new Date(result.delivery_date).toLocaleDateString(undefined, {
              weekday: "long", day: "numeric", month: "long",
            })}
          </span>{" "}
          · Free · Insured · {result.carrier}
          {result.ddp ? " · DDP, duties paid" : ""}
          <span className="mt-1 block text-xs text-dim">{result.note}</span>
        </p>
      )}
    </div>
  );
}

function HoroscopeBlock({ product }) {
  const [form, setForm] = useState({
    wearer_name: "", dob: "", time_of_birth: "", place_of_birth: "", gender: "", gotra: "",
    purpose: "general", email: "",
  });
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) {
      toast.error("We cannot use your birth details without consent. Nothing has been sent.");
      return;
    }
    setBusy(true);
    try {
      const r = await postHoroscopeCheck({
        product_slug: product.slug, ...form, consent_chart_preparation: consent,
      });
      setDone(r);
      toast.success("Sent to a consultant.");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="card-s border-l-2 border-l-verify p-5" data-testid="horoscope-done">
        <H3>Reference <span className="num">{done.reference}</span></H3>
        <p className="ui mt-2 text-sm text-dim">{done.message}</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="card-s p-5" data-testid="horoscope-block">
      <p className="eyebrow mb-2">Want us to check this is right for you before we ship?</p>
      <p className="ui mb-4 text-sm text-dim">
        A consultant will look at your chart and confirm — or tell you it isn't suitable and refund you
        in full. Free, no obligation, and they earn nothing either way.
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="field-label">Wearer's name</span>
          <input required value={form.wearer_name} onChange={set("wearer_name")} className="field" data-testid="horoscope-name" />
        </label>
        <label className="block">
          <span className="field-label">Date of birth</span>
          <input required type="date" value={form.dob} onChange={set("dob")} className="field num" data-testid="horoscope-dob" />
        </label>
        <label className="block">
          <span className="field-label">Time of birth</span>
          <input type="time" value={form.time_of_birth} onChange={set("time_of_birth")} className="field num" data-testid="horoscope-time" />
        </label>
        <label className="block">
          <span className="field-label">Place of birth</span>
          <input required value={form.place_of_birth} onChange={set("place_of_birth")} className="field" data-testid="horoscope-place" />
        </label>
        <label className="block">
          <span className="field-label">Gender</span>
          <select value={form.gender} onChange={set("gender")} className="field ui">
            <option value="">Prefer not to say</option>
            <option value="female">Female</option>
            <option value="male">Male</option>
            <option value="other">Other</option>
          </select>
        </label>
        <label className="block">
          <span className="field-label">Gotra (optional)</span>
          <input value={form.gotra} onChange={set("gotra")} className="field" />
        </label>
        <label className="block">
          <span className="field-label">Primary purpose</span>
          <select value={form.purpose} onChange={set("purpose")} className="field ui" data-testid="horoscope-purpose">
            <option value="general">General</option>
            <option value="career-work">Career & work</option>
            <option value="business-wealth">Business & wealth</option>
            <option value="relationships">Relationships & marriage</option>
            <option value="study-learning">Study & learning</option>
            <option value="health-wellbeing">Health & wellbeing</option>
            <option value="peace-of-mind">Peace of mind</option>
          </select>
        </label>
        <label className="block">
          <span className="field-label">Your email</span>
          <input required type="email" value={form.email} onChange={set("email")} className="field" data-testid="horoscope-email" />
        </label>
      </div>

      <label htmlFor="horoscope-consent" className="mt-5 flex cursor-pointer items-start gap-3">
        <input
          id="horoscope-consent"
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          data-testid="horoscope-consent"
          className="mt-1 h-4 w-4 shrink-0 accent-[var(--c-gold)]"
        />
        <span className="ui text-xs leading-relaxed text-dim">
          I agree that Shreem Mantra Energy may use my birth date, time and place solely to prepare my
          chart and advise on this purchase. I understand this data will not be sold, will not be shared
          with any other consultant, and will not be used for advertising. I can delete it at any time
          from my account or by emailing {COMPANY.privacy_email}.{" "}
          <Link to="/legal/consent-notice" className="link-u">Read the consent notice →</Link>
        </span>
      </label>

      <button type="submit" disabled={busy} className="btn btn-secondary mt-5" data-testid="horoscope-submit">
        {busy ? "Sending…" : "Ask a consultant to check"}
      </button>
    </form>
  );
}

function CostBreakdown({ product }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="card-s" data-testid="cost-breakdown">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        data-testid="cost-breakdown-toggle"
        className="ui flex w-full items-center justify-between gap-4 p-5 text-left text-sm font-medium text-ink"
      >
        Why this costs what it costs
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="cost-breakdown-body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.34, ease: EASE_OUT }}
            className="overflow-hidden border-t border-rule"
          >
            <div className="p-5">
              <table className="w-full text-sm">
                <tbody>
                  {(product.cost_breakdown || []).map((r) => (
                    <tr key={r.label} className="border-b border-rule/70 last:border-0">
                      <td className="ui py-2.5 pr-4 text-dim">{r.label}</td>
                      <td className="num py-2.5 text-right text-ink">{r.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="ui mt-4 text-xs leading-[1.7] text-dim">
                Publishing a margin band is unheard of in this category, and it is exactly what defuses the
                accusation that people in this trade can sell anything to anybody.
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Product() {
  const { slug } = useParams();
  const { format, code } = useCurrency();
  const cart = useCart();
  const [tab, setTab] = useState("description");
  const [declineEnergisation, setDeclineEnergisation] = useState(false);
  const [addonIds, setAddonIds] = useState([]);

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ["product", slug],
    queryFn: () => getProduct(slug),
    retry: (n, e) => (e?.response?.status === 404 ? false : n < 2),
  });

  const p = data?.product;

  // Tabs hide themselves when the field they'd render is empty — keeps the
  // product page clean for the semi-precious launch batch whose descriptions
  // are left blank until the owner writes them.
  const tabs = useMemo(() => {
    if (!p) return [];
    const list = [];
    if (p.description) list.push(["description", "Description"]);
    if (p.tradition) list.push(["tradition", "In the tradition"]);
    if (p.how_to_wear) list.push(["wear", "How to wear"]);
    if (p.care) list.push(["care", "Care"]);
    if (p.certificate) list.push(["certificate", "Certificate"]);
    // Reviews tab is always available (users can leave a review on any product).
    list.push(["reviews", "Questions & reviews"]);
    return list;
  }, [p]);

  // If the current tab is hidden for this product, snap to the first available.
  useEffect(() => {
    if (!tabs.length) return;
    if (!tabs.some(([id]) => id === tab)) {
      setTab(tabs[0][0]);
    }
  }, [tabs, tab]);

  const trail = useMemo(() => {
    if (!p) return [];
    const t = [{ label: "Home", to: "/" }, { label: "Shop", to: "/shop" }];
    if (p.type === "gemstone") {
      t.push({ label: "Gemstones", to: "/shop/gemstones" });
      if (p.stone_slug) t.push({ label: p.title.split(" (")[0], to: `/shop/gemstones/${p.stone_slug}` });
    } else if (p.type === "rudraksha") {
      t.push({ label: "Rudraksha", to: "/shop/rudraksha" });
      t.push({ label: `${p.rudraksha?.mukhi} mukhi`, to: `/shop/rudraksha/${p.rudraksha?.mukhi}-mukhi` });
    }
    t.push({ label: p.sku });
    return t;
  }, [p]);

  useSEO({
    title: p
      ? p.gemstone
        ? `${p.title} ${p.gemstone.carat} ct / ${p.gemstone.ratti} ratti, ${p.gemstone.origin.split(" (")[0]}, ${p.gemstone.treatment}${p.certificate ? ` — ${p.certificate.lab} ${p.certificate.report_number}` : ""}`
        : p.title + " — " + (p.subtitle || "")
      : "Product",
    description: p
      ? `${p.description.slice(0, 240)}`
      : undefined,
    jsonLd: p
      ? [
          breadcrumbSchema(trail),
          {
            "@context": "https://schema.org",
            "@type": "Product",
            name: p.title,
            sku: p.sku,
            description: p.description,
            image: (p.images || []).map((i) => i.url),
            brand: { "@type": "Brand", name: "Shreem Mantra Energy" },
            material: p.gemstone?.species || undefined,
            color: p.gemstone?.colour || undefined,
            weight: p.gemstone ? { "@type": "QuantitativeValue", value: p.gemstone.carat, unitText: "carat" } : undefined,
            offers: {
              "@type": "Offer",
              price: p.price_inr,
              priceCurrency: "INR",
              availability: p.status === "sold"
                ? "https://schema.org/SoldOut"
                : "https://schema.org/InStock",
              url: `https://shreemmantraenergy.com/product/${p.slug}`,
            },
            ...(data?.reviews?.length
              ? {
                  aggregateRating: {
                    "@type": "AggregateRating",
                    ratingValue: (data.reviews.reduce((s, r) => s + r.rating, 0) / data.reviews.length).toFixed(1),
                    reviewCount: data.reviews.length,
                  },
                }
              : {}),
          },
        ]
      : undefined,
  });

  if (isLoading) {
    return (
      <Container className="py-20" data-testid="product-skeleton" aria-busy="true">
        <div className="grid gap-10 lg:grid-cols-[3fr_2fr]">
          <div className="aspect-square animate-pulse bg-surface" />
          <div className="space-y-4">
            <div className="h-4 w-20 animate-pulse bg-surface" />
            <div className="h-10 w-3/4 animate-pulse bg-surface" />
            <div className="h-4 w-1/2 animate-pulse bg-surface" />
            <div className="h-24 animate-pulse bg-surface" />
            <div className="h-8 w-40 animate-pulse bg-surface" />
          </div>
        </div>
      </Container>
    );
  }
  if (isError || !p) {
    const status = error?.response?.status;
    if (status === 404) {
      return (
        <Container className="py-20">
          <EmptyState title="We could not find that item">
            We never delete a product URL, so this is almost certainly a typo.{" "}
            <Link to="/shop" className="link-u">Back to the shop →</Link>{" "}
            or the{" "}
            <Link to="/shop/sold-archive" className="link-u">Sold archive →</Link>
          </EmptyState>
        </Container>
      );
    }
    return (
      <Container className="py-20">
        <div className="border border-rule bg-surface p-8 text-center" data-testid="product-error">
          <p className="menu-h !border-b-0 !pb-0 !mb-3">Couldn't load this stone just now</p>
          <p className="mx-auto max-w-lg text-[15px] leading-[1.75] text-dim">
            Our catalogue service didn't answer. Usually a brief blip — try again.
          </p>
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="btn btn-primary mt-6"
            data-testid="product-retry"
          >
            {isFetching ? "Retrying…" : "Try again"}
          </button>
        </div>
      </Container>
    );
  }

  const sold = p.status === "sold";
  const availableAddons = data?.addons || [];
  const selectedAddons = availableAddons.filter((a) => addonIds.includes(a.id));
  const addonsTotal = selectedAddons.reduce((s, a) => s + a.price_inr, 0);

  const add = () => {
    cart.add(p, {
      energisation: !declineEnergisation,
      addons: selectedAddons.map((a) => ({ id: a.id, label: a.label, price_inr: a.price_inr })),
    });
    toast.success("Added. Nothing further will be added at checkout.");
  };

  const grahaLabel = p.graha ? p.graha.charAt(0).toUpperCase() + p.graha.slice(1) : null;
  const amb = p.ambient || {};
  const atmoStyle = {
    "--amb-1": amb.amb1 || "#3d2f14",
    "--amb-2": amb.amb2 || "#1a130a",
  };

  return (
    <div className="product-atmosphere" style={atmoStyle} data-testid="product-atmosphere">
      <Container className="pt-6"><Breadcrumbs trail={trail} /></Container>

      <Container className="grid items-start gap-10 py-8 lg:grid-cols-2 lg:gap-14">
        <Gallery product={p} />

        <div>
          {/* 2 — title block */}
          <div data-testid="title-block">
            <H1>{p.title}</H1>
            <p className="ui num mt-2 text-sm text-dim">
              {p.subtitle}
              {p.gemstone ? ` · ${p.gemstone.carat} ct / ${p.gemstone.ratti} ratti` : ""}
            </p>

            {/* treatment above the fold, always */}
            {p.gemstone && (
              <p className="ui mt-4 border-l-2 border-l-gold bg-surface px-4 py-3 text-sm text-ink" data-testid="treatment-above-fold">
                <strong>Treatment: {p.gemstone.treatment}.</strong> {p.gemstone.treatment_detail}
              </p>
            )}

            {/* Certificate — rendered only for products that actually carry one.
                Semi-precious items don't ship with a lab certificate. */}
            {p.certificate && (
              <div className="mt-5">
                <CertificateBlock certificate={p.certificate} />
              </div>
            )}
            <p className="num mt-3 text-xs text-dim">SKU {p.sku}</p>
          </div>

          {/* 3 — price block */}
          <div className="mt-8 border-t border-rule pt-6" data-testid="price-block">
            {p.price_inr == null ? (
              <>
                <p
                  className="ui text-[20px] font-semibold uppercase tracking-[0.08em] text-gold"
                  data-testid="price-on-request"
                >
                  Price on request
                </p>
                <p className="ui mt-2 text-xs text-dim">
                  This piece doesn't carry a published price yet. Message us for the current rate
                  and availability — we'll reply within one working day.
                </p>
              </>
            ) : (
              <>
                <p className="num text-[28px] font-semibold leading-none text-deep">{format(p.price_inr)}</p>
                {p.price_per_carat_inr && (
                  <p className="num mt-1.5 text-sm text-dim">{format(p.price_per_carat_inr)} per carat</p>
                )}
                <p className="ui mt-2 text-xs text-dim">
                  Inclusive of tax. {code !== "INR"
                    ? "Landed price for your country, duties and destination taxes paid. Nothing more to pay on delivery."
                    : "Certification, energisation and insured shipping are included, never an upgrade."}
                </p>
              </>
            )}
            {!sold && p.price_inr != null && p.type !== "semi-precious" && (
              <p className="ui mt-3 text-xs text-saffron" data-testid="one-piece-line">
                One piece. When it sells, this page becomes its permanent archive.
              </p>
            )}
            {!sold && p.stock_display === "in-stock" && (
              <p className="ui mt-3 text-xs text-dim" data-testid="in-stock-line">
                In stock.
              </p>
            )}
          </div>

          {/* 5 — add to cart (or enquire, when no price is set) */}
          <div className="mt-6">
            {sold ? (
              <div className="card-s p-5" data-testid="sold-block">
                <H3>Sold — and this page stays exactly where it is</H3>
                <p className="mt-2 text-[15px] leading-[1.75] text-dim">
                  This stone existed once. We do not restock it, because no two natural stones are alike.
                  The certificate above still verifies on the laboratory's own site, which is the point of
                  keeping the page.
                </p>
                <Link to="/shop/gemstones" className="link-u ui mt-3 inline-block text-sm">
                  See what we currently hold →
                </Link>
              </div>
            ) : p.price_inr == null ? (
              <div data-testid="enquire-block">
                <Link to="/contact" className="btn btn-primary" data-testid="enquire-cta">
                  Enquire about this piece
                </Link>
                <p className="ui mt-3 text-xs text-dim">
                  Checkout opens here once a price is published.
                </p>
              </div>
            ) : (
              <>
                <div className="flex flex-wrap gap-3">
                  {cart.inCart(p.slug) ? (
                    <Link to="/cart" className="btn btn-primary" data-testid="in-cart">
                      In your cart — view cart
                    </Link>
                  ) : (
                    <button type="button" onClick={add} className="btn btn-primary" data-testid="add-to-cart">
                      Add this stone to cart
                    </button>
                  )}
                  <Link to="/cart" onClick={add} className="btn btn-secondary" data-testid="buy-now">
                    Buy now
                  </Link>
                </div>
                <GuaranteeLine testId="product-guarantee" className="mt-4">
                  30-day independent re-test guarantee — if any lab disagrees with our report, full
                  refund and we pay your lab fee.
                </GuaranteeLine>
                <p className="ui mt-2 text-xs text-dim">
                  Shipping and any duties are calculated at checkout before you pay. Dispatched within
                  72 hours or your money back automatically.
                </p>
              </>
            )}
          </div>

          {/* Add-ons — data-driven "Complete this piece".
              Hidden for price-on-request products (no checkout path yet). */}
          {!sold && p.price_inr != null && availableAddons.length > 0 && (
            <div className="mt-8" data-testid="addons-module">
              <p className="menu-h !border-b-0 !pb-0 !mb-3">Complete this piece</p>
              <div className="grid gap-2">
                {availableAddons.map((a) => {
                  const on = addonIds.includes(a.id);
                  return (
                    <label
                      key={a.id}
                      htmlFor={`addon-${a.id}`}
                      className={`fbox cursor-pointer border p-4 transition-colors ${on ? "border-gold bg-wash" : "border-rule hover:border-gold/60"}`}
                      data-testid={`addon-${a.id}`}
                    >
                      <input
                        id={`addon-${a.id}`}
                        type="checkbox"
                        className="sr-only"
                        checked={on}
                        onChange={() => setAddonIds((ids) => on ? ids.filter((x) => x !== a.id) : [...ids, a.id])}
                      />
                      <span className="fbox__box" aria-hidden="true"><Check strokeWidth={3} /></span>
                      <span className="flex-1">
                        <span className="ui block text-sm font-medium text-ink">
                          {a.label} <span className="num text-dim">+{format(a.price_inr)}</span>
                        </span>
                        <span className="ui mt-1 block text-xs leading-[1.5] text-dim">{a.description}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
              {addonIds.length > 0 && (
                <p className="ui mt-2 text-xs text-verify" data-testid="addons-total">
                  +{format(addonsTotal)} added transparently — no pre-ticked boxes, remove any time.
                </p>
              )}
            </div>
          )}

          {/* 6 — Setting radio group removed (D1). Ring setting/pendant setting are
              now offered exclusively through the server-authoritative "Complete
              this piece" add-ons block above, and any customer who wants a
              specific ring size or metal picks it via /customize. This deletes
              the second, conflicting price source for the same items. */}

          {/* 7 — energisation (gemstone / rudraksha / mala / bracelet only —
              the semi-precious batch doesn't carry a graha claim) */}
          {p.type !== "semi-precious" && (
          <div className="card-s mt-6 p-6" data-testid="energisation-block">
            <p className="menu-h !border-b-0 !pb-0 !mb-2">Energisation — included, and explained</p>
            <p className="text-[15px] leading-[1.7] text-dim">
              Energisation is included at no charge. Your item will be energised with the mantra
              traditionally associated with {grahaLabel || "the presiding graha"} — you'll receive a
              short video of it being done, with the date visible.
            </p>
            <label htmlFor="decline-energisation" className="mt-4 flex cursor-pointer items-start gap-3">
              <input
                id="decline-energisation"
                type="checkbox"
                checked={declineEnergisation}
                onChange={(e) => setDeclineEnergisation(e.target.checked)}
                data-testid="decline-energisation"
                className="mt-1 h-4 w-4 accent-[var(--c-gold)]"
              />
              <span className="ui consent-copy text-xs text-dim">
                Not everyone wants this. If you'd rather receive the item unenergised, tick here — it
                costs the same either way.
              </span>
            </label>
            <p className="ui mt-4 border-t border-rule pt-4 text-xs leading-[1.6] text-dim">
              <strong className="text-ink">Why we don't charge for it:</strong> one large competitor runs
              an energisation ladder from free up to a paid live-streamed puja. Charging separately for
              something we describe as essential is drip pricing, which is a named dark pattern under
              Indian consumer-protection guidelines. So it's included, and the proof is included too.
            </p>
          </div>
          )}

          {/* 8 — horoscope capture (not applicable to the semi-precious batch,
              which carries no graha linkage) */}
          {p.type !== "semi-precious" && (
            <div className="mt-6"><HoroscopeBlock product={p} /></div>
          )}

          {/* 9 — delivery */}
          <div className="mt-6"><DeliveryBlock /></div>

          {/* 10 — trust strip + honest scarcity */}
          <div className="mt-6 border-t border-rule pt-6" data-testid="trust-strip">
            <ul className="ui grid grid-cols-2 gap-3 text-xs text-dim sm:grid-cols-3">
              <li className="flex items-center gap-2"><RotateCcw className="h-3.5 w-3.5 text-verify" />30-day returns</li>
              <li className="flex items-center gap-2"><Truck className="h-3.5 w-3.5 text-verify" />Free insured shipping</li>
              <li className="flex items-center gap-2"><ShieldCheck className="h-3.5 w-3.5 text-verify" />Verified on the lab's own site</li>
              <li className="flex items-center gap-2"><ScanLine className="h-3.5 w-3.5 text-verify" />Loose-first inspection</li>
              <li className="flex items-center gap-2"><MessageCircle className="h-3.5 w-3.5 text-verify" />WhatsApp us</li>
              <li className="flex items-center gap-2"><Phone className="h-3.5 w-3.5 text-verify" />Call us during working hours</li>
            </ul>
            {p.type === "gemstone" && (
              <p className="mt-5 border-l-2 border-l-gold pl-4 text-[15px] italic leading-[1.75] text-dim" data-testid="honest-scarcity">
                This stone exists once. Not "only 2 left in stock" — there is, in the most literal sense,
                one of these in the world. When it's gone, we don't restock it, because no two natural
                stones are alike. What replaces it is a different stone, at a different price.
              </p>
            )}
          </div>

          {/* Guided next-step cards */}
          {!sold && (
            <div className="mt-8 grid gap-3" data-testid="guided-ctas">
              {p.type === "gemstone" && (
                <Link
                  to={`/customize?sku=${encodeURIComponent(p.sku)}&slug=${encodeURIComponent(p.slug)}`}
                  className="group card-s block p-5 transition-colors duration-200 hover:border-gold"
                  data-testid="guided-cta-customize"
                >
                  <p className="menu-h !border-b-0 !pb-0 !mb-2">Make it yours to wear</p>
                  <p className="ui flex items-start justify-between gap-3 text-sm leading-[1.6] text-dim">
                    <span>
                      Have this stone set as a ring or pendant.{" "}
                      <span className="text-gold group-hover:underline">Start the setting consultation →</span>
                    </span>
                    <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-dim transition-transform group-hover:translate-x-0.5 group-hover:text-gold" />
                  </p>
                </Link>
              )}
              <Link
                to="/consult/vedic-astrology"
                className="group card-s block p-5 transition-colors duration-200 hover:border-gold"
                data-testid="guided-cta-report"
              >
                <p className="menu-h !border-b-0 !pb-0 !mb-2">Want the full written analysis for your chart?</p>
                <p className="ui flex items-start justify-between gap-3 text-sm leading-[1.6] text-dim">
                  <span>
                    The Full Chart Session — its written analysis is the report.{" "}
                    <span className="text-gold group-hover:underline">Get your report →</span>
                  </span>
                  <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-dim transition-transform group-hover:translate-x-0.5 group-hover:text-gold" />
                </p>
              </Link>
            </div>
          )}
        </div>
      </Container>

      {/* Below the fold — new seven-section wall when content_v2 is authored;
          fall back to the legacy tabbed layout otherwise so every product in
          the catalogue continues to render while the founder rolls copy out. */}
      {p.content_v2 ? (
        <ProductContentV2 product={p} cv={p.content_v2} />
      ) : (
      <Section className="border-t border-rule bg-surface">
        <Container>
          <div className="no-scrollbar flex gap-1 overflow-x-auto border-b border-rule" role="tablist">
            {tabs.map(([id, label]) => (
              <button
                key={id}
                role="tab"
                aria-selected={tab === id}
                data-testid={`tab-${id}`}
                onClick={() => setTab(id)}
                className={`ui shrink-0 border-b-2 px-4 py-3 text-sm transition-colors ${
                  tab === id ? "border-gold text-gold" : "border-transparent text-dim hover:text-ink"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="grid items-start gap-10 pt-10 lg:grid-cols-[1.1fr_0.9fr] lg:gap-14">
            <AnimatePresence mode="wait">
              <motion.div
                key={tab}
                role="tabpanel"
                data-testid={`panel-${tab}`}
                variants={panelSwap}
                initial="hidden"
                animate="show"
                exit="exit"
              >
              {tab === "description" && (
                <>
                  <H2>Description</H2>
                  <p className="measure ui mt-4 text-sm leading-relaxed text-dim">{p.description}</p>
                </>
              )}
              {tab === "tradition" && (
                <>
                  <H2>In the tradition</H2>
                  <p className="measure ui mt-4 text-sm leading-relaxed text-dim">{p.tradition}</p>
                  <p className="measure ui mt-4 text-xs text-dim">
                    Descriptions of traditional significance reflect classical literature, belief and
                    custom. They are not statements of scientific, medical or psychological fact, and no
                    outcome is guaranteed.
                  </p>
                </>
              )}
              {tab === "wear" && (
                <>
                  <H2>How to wear it</H2>
                  <p className="measure ui mt-4 text-sm leading-relaxed text-dim">{p.how_to_wear}</p>
                </>
              )}
              {tab === "care" && (
                <>
                  <H2>Care & cleaning</H2>
                  <p className="measure ui mt-4 text-sm leading-relaxed text-dim">{p.care}</p>
                </>
              )}
              {tab === "certificate" && (
                <>
                  <H2>Certificate</H2>
                  {p.certificate ? (
                    <div className="mt-4">
                      <img
                        src={p.certificate.image_url}
                        alt={`${p.certificate.lab} report ${p.certificate.report_number}`}
                        className="w-full max-w-xl border border-rule"
                      />
                      <div className="mt-4 flex flex-wrap gap-3">
                        <a href={p.certificate.pdf_url} target="_blank" rel="noopener noreferrer" className="btn btn-secondary" data-testid="certificate-pdf">
                          Download the report PDF
                        </a>
                        <a
                          href={p.certificate.verification_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-primary"
                          data-testid="certificate-verify"
                        >
                          Verify on {new URL(p.certificate.verification_url).hostname.replace("www.", "")}
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      </div>
                      <p className="ui mt-4 text-xs text-dim">
                        The verification link opens the laboratory's own website in a new tab. It is never a
                        page on our domain — that is enforced in code, not in policy.
                      </p>
                    </div>
                  ) : (
                    <div className="mt-4"><SelfDescribedBlock product={p} /></div>
                  )}
                </>
              )}
              {tab === "reviews" && (
                <>
                  <H2>Questions & reviews</H2>
                  {data.reviews?.length ? (
                    <ul className="mt-4 divide-y divide-rule">
                      {data.reviews.map((r) => (
                        <li key={`${r.author}-${r.date}`} className="py-4">
                          <H3>{r.title}</H3>
                          <p className="mt-1 text-[15px] leading-[1.75] text-dim">{r.body}</p>
                          <p className="ui mt-2 text-xs text-dim">{r.author} · {r.location} · Verified purchase</p>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mt-4 text-[15px] leading-[1.75] text-dim">
                      No reviews for this item yet, and we will not borrow a rating from another product to
                      fill the gap. Verified buyers can post here after purchase, and negative reviews stay
                      up.
                    </p>
                  )}
                </>
              )}
            </motion.div>
            </AnimatePresence>

            {(p.gemstone || p.rudraksha || (p.cost_breakdown || []).length > 0) && (
              <div className="space-y-6 lg:sticky lg:top-28 lg:self-start">
                {(p.gemstone || p.rudraksha) && (
                  <div className="card-s elev p-5">
                    <p className="eyebrow mb-3">Specification</p>
                    <SpecTable product={p} />
                  </div>
                )}
                {(p.cost_breakdown || []).length > 0 && <CostBreakdown product={p} />}
              </div>
            )}
          </div>
        </Container>
      </Section>
      )}

      {/* Related */}
      {data.related?.length > 0 && (
        <Section>
          <Container>
            <Reveal>
              <H2 className="mb-10">Related — same species, same graha</H2>
            </Reveal>
            <ProductGrid products={data.related} testId="related-grid" />
          </Container>
        </Section>
      )}
    </div>
  );
}
